import * as React from 'react';
import { size as _size, filter, isNil } from 'es-toolkit/compat';

import { Button } from 'omakase';
import { CheckoutOrigin } from 'state/selection';
import { core } from '@tock/coretypes';
import { isDeliveryVariety, isOrderVariety } from '@tock/shared/utils/offerings';
import { isPrimaryPatron } from 'utils/purchase';
import { useFeatureFlag } from '@tock/shared/utils/featureFlagContext';
import Anchor from 'components/Anchor';
import ConsumerDisplayMoney from 'components/ConsumerDisplayMoney';

interface ConfirmOptionsDialogActionsProps {
  business: core.IBusiness;
  ticketType: core.ITicketType;
  purchase: core.PurchaseOrder;
  onClose(): void;
  isVisitEdit: boolean;
  patron?: core.IPatron;
  onComplete(): void;
  onSubmitOrder(): void;
  confirmDisabled?: boolean;
  submitting?: boolean;
  itemCount: number;
  partyOptionOrders?: core.IOptionOrder[];
  optionsSentToKitchen?: boolean;
  checkoutOrigin?: CheckoutOrigin;
  allowOrderConfirmation: boolean;
}

const ConfirmOptionsDialogActions: React.FunctionComponent<ConfirmOptionsDialogActionsProps> = ({
  allowOrderConfirmation,
  ticketType,
  business,
  purchase,
  onClose,
  isVisitEdit,
  patron,
  onComplete,
  onSubmitOrder,
  confirmDisabled,
  itemCount,
  submitting,
  partyOptionOrders,
  optionsSentToKitchen,
}) => {
  const allowZeroDollarTTG = useFeatureFlag('ALLOW_ZERO_DOLLAR_TTG');
  const alowZeroDollarDineIn = useFeatureFlag('ALLOW_ZERO_DOLLAR_DINE_IN');

  const partyOptionsSentToKitchen =
    _size(filter(partyOptionOrders, (order) => !!order.sentToKitchen)) > 0;

  const isDeliveryAndBelowMinimum =
    isDeliveryVariety(ticketType.variety) &&
    !isNil(business.minDeliveryOrderSizeThresholdCents) &&
    purchase.subtotal < business.minDeliveryOrderSizeThresholdCents;

  const notDeliveryAndBelowMinimum =
    !isNil(ticketType.orderMinimumCents) && purchase.subtotal < ticketType.orderMinimumCents;

  const isOrderAndZeroSubtotalNotAllowed =
    purchase.subtotal === 0 &&
    ((isOrderVariety(ticketType.variety) && !allowZeroDollarTTG) ||
      (!isOrderVariety(ticketType.variety) && !alowZeroDollarDineIn));

  if (isDeliveryAndBelowMinimum) {
    return (
      <Button variant="primary" size="large" disabled={true} fullWidth onClick={onClose}>
        Delivery minimum&nbsp;
        <ConsumerDisplayMoney amount={business.minDeliveryOrderSizeThresholdCents} />
      </Button>
    );
  }

  if (notDeliveryAndBelowMinimum) {
    return (
      <Button variant="primary" size="large" disabled={true} fullWidth onClick={onClose}>
        Order minimum&nbsp;
        <ConsumerDisplayMoney amount={ticketType.orderMinimumCents} />
      </Button>
    );
  }

  if (isOrderAndZeroSubtotalNotAllowed) {
    return (
      <Button variant="primary" size="large" fullWidth onClick={onClose}>
        Add items to continue
      </Button>
    );
  }

  if (isVisitEdit) {
    return (
      <>
        {(optionsSentToKitchen || partyOptionsSentToKitchen) &&
          purchase.totalPrice > purchase.netAmountPaidCents &&
          isPrimaryPatron(purchase, patron) && (
            <Button
              size="large"
              variant="ghost"
              disabled={confirmDisabled || submitting}
              fullWidth
              component={Anchor}
              href={`/${business?.domainName}/tableside/payment/?token=${encodeURIComponent(
                purchase.visitEditToken
              )}`}
            >
              or Continue to pay now
            </Button>
          )}
        <Button
          size="large"
          variant="primary"
          disabled={confirmDisabled || submitting || itemCount === 0}
          fullWidth
          onClick={onSubmitOrder}
        >
          {itemCount === 0
            ? 'Add items to order'
            : optionsSentToKitchen
              ? 'Submit new items'
              : 'Submit order'}
        </Button>
      </>
    );
  }
  return (
    <Button
      size="large"
      variant="primary"
      disabled={confirmDisabled}
      fullWidth
      onClick={allowOrderConfirmation ? onComplete : onClose}
    >
      {allowOrderConfirmation ? `Continue${purchase.totalPrice > 0 ? ' to payment' : ''}` : 'Done'}
    </Button>
  );
};

export default ConfirmOptionsDialogActions;
